import { useMemo, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Sparkles, Loader2, Check, X, Camera, ArrowUp, ArrowDown, RotateCw, Images, FileText } from "lucide-react";
import { InfoTip } from "@/components/InfoTip";

// Charge Storyboard: organizes the user's OWN sealed evidence for a charge. Never evaluates the charge.
export function ChargeStoryboard({ charge: c, propertyId, index, archived, onPatch }) {
  const [suggestions, setSuggestions] = useState(null); // null=not fetched, []=none
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [noSealed, setNoSealed] = useState(false);
  const [selected, setSelected] = useState({});
  const [dismissed, setDismissed] = useState({});
  const [candidates, setCandidates] = useState(null);
  const [roomFilter, setRoomFilter] = useState("all");
  const [note, setNote] = useState(c.storyboard_note || "");

  const matchedIds = useMemo(() => c.matched_evidence_ids || [], [c.matched_evidence_ids]);
  const photoRefs = useMemo(() => matchedIds.filter((id) => (index[id] || {}).type === "photo"), [matchedIds, index]);
  const supporting = useMemo(() => matchedIds.filter((id) => index[id] && index[id].type !== "photo"), [matchedIds, index]);

  const patchIds = (next) => onPatch(c.id, { matched_evidence_ids: next });

  const attach = (photoId) => { if (!matchedIds.includes(photoId)) patchIds([...matchedIds, photoId]); };
  const detach = (photoId) => patchIds(matchedIds.filter((x) => x !== photoId));
  const move = (photoId, dir) => {
    const order = [...matchedIds];
    const i = order.indexOf(photoId);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= order.length) return;
    [order[i], order[j]] = [order[j], order[i]];
    patchIds(order);
  };

  const runSuggest = async () => {
    setLoading(true); setError(false); setNoSealed(false);
    try {
      const { data } = await api.post(`/charges/${c.id}/storyboard/suggest-photos`, {});
      if (data.no_sealed_photos) { setNoSealed(true); setSuggestions([]); }
      else setSuggestions(data.suggestions || []);
    } catch (e) {
      if (e?.response?.status === 429) toast.error(e.response.data?.detail || "Too many requests today.");
      else setError(true);
    } finally { setLoading(false); }
  };

  const loadCandidates = async () => {
    try {
      const { data } = await api.get(`/charges/${c.id}/storyboard/candidates`);
      if (data.no_sealed_photos) setNoSealed(true);
      setCandidates(data.candidates || []);
    } catch { toast.error("Couldn't load your photos."); }
  };

  const attachSelected = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k] && !matchedIds.includes(k));
    if (!ids.length) { toast.info("Select at least one photo first."); return; }
    await patchIds([...matchedIds, ...ids]);
    setSelected({});
    toast.success(`Attached ${ids.length} photo${ids.length > 1 ? "s" : ""}.`);
  };

  const openSuggestions = (suggestions || []).filter((s) => !matchedIds.includes(s.photo_id) && !dismissed[s.photo_id]);
  const rooms = useMemo(() => Array.from(new Set((candidates || []).map((p) => p.room))), [candidates]);
  const visibleCandidates = (candidates || []).filter((p) => !matchedIds.includes(p.photo_id) && (roomFilter === "all" || p.room === roomFilter));

  return (
    <div className="mt-3 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-3" data-testid={`storyboard-${c.id}`}>
      <div className="flex items-center gap-1.5">
        <Images size={14} className="text-brand" />
        <span className="text-xs font-bold uppercase tracking-wide text-[#475569]">Charge storyboard</span>
        <InfoTip topic="chargeStoryboard" />
      </div>

      {supporting.length > 0 && (
        <div className="mt-2" data-testid={`storyboard-supporting-${c.id}`}>
          <p className="text-[11px] font-semibold text-[#64748B]">Supporting records</p>
          <div className="mt-1 space-y-1">
            {supporting.map((id) => (
              <div key={id} className="flex items-center gap-1.5 text-xs text-[#334155] bg-white rounded-lg px-2 py-1 border border-[#EEF2F6]" data-testid={`storyboard-support-${c.id}-${id}`}>
                <FileText size={11} className="text-[#94A3B8] shrink-0" />
                <span className="truncate">{(index[id] || {}).label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Attached photos */}
      {photoRefs.length > 0 ? (
        <div className="mt-2.5 grid grid-cols-3 gap-2" data-testid={`storyboard-photos-${c.id}`}>
          {photoRefs.map((id, i) => (
            <div key={id} className="relative rounded-lg overflow-hidden border border-[#E2E8F0] bg-white" data-testid={`storyboard-photo-${c.id}-${id}`}>
              <img src={fileUrl((index[id] || {}).image_path)} alt="" className="w-full h-20 object-cover" />
              <div className="px-1 py-0.5 text-[9px] text-[#475569] truncate">{(index[id] || {}).label || "Sealed photo"}</div>
              {!archived && (
                <div className="absolute top-0.5 right-0.5 flex gap-0.5">
                  <button data-testid={`storyboard-up-${c.id}-${id}`} onClick={() => move(id, -1)} disabled={i === 0} className="grid place-items-center h-5 w-5 rounded bg-white/90 text-navy disabled:opacity-30"><ArrowUp size={11} /></button>
                  <button data-testid={`storyboard-down-${c.id}-${id}`} onClick={() => move(id, 1)} disabled={i === photoRefs.length - 1} className="grid place-items-center h-5 w-5 rounded bg-white/90 text-navy disabled:opacity-30"><ArrowDown size={11} /></button>
                  <button data-testid={`storyboard-remove-${c.id}-${id}`} onClick={() => detach(id)} className="grid place-items-center h-5 w-5 rounded bg-white/90 text-red-500"><X size={11} /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        !archived && (
          <div className="mt-2.5 rounded-lg border border-dashed border-[#CBD5E1] bg-white p-3 text-center" data-testid={`storyboard-empty-${c.id}`}>
            <Camera size={18} className="text-[#94A3B8] mx-auto" />
            <p className="mt-1 text-xs font-medium text-navy">Add your documentation for this charge</p>
            <div className="mt-2 flex flex-wrap justify-center gap-2">
              <Button data-testid={`storyboard-suggest-${c.id}`} size="sm" onClick={runSuggest} disabled={loading} className="h-8 rounded-lg bg-navy hover:bg-navy-hover text-white text-xs">
                {loading ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Sparkles size={13} className="mr-1" />} Suggest matching photos
              </Button>
              <Button data-testid={`storyboard-manual-${c.id}`} size="sm" variant="outline" onClick={() => { setCandidates(candidates || null); loadCandidates(); }} className="h-8 rounded-lg text-xs">Choose photos manually</Button>
            </div>
          </div>
        )
      )}

      {/* add-more entry when photos already exist */}
      {photoRefs.length > 0 && !archived && (
        <div className="mt-2 flex flex-wrap gap-2">
          <Button data-testid={`storyboard-suggest-more-${c.id}`} size="sm" variant="outline" onClick={runSuggest} disabled={loading} className="h-7 rounded-lg text-xs">
            {loading ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Sparkles size={12} className="mr-1" />} Suggest
          </Button>
          <Button data-testid={`storyboard-manual-more-${c.id}`} size="sm" variant="outline" onClick={loadCandidates} className="h-7 rounded-lg text-xs">Choose manually</Button>
        </div>
      )}

      {error && (
        <div className="mt-2 rounded-lg border border-red-200 bg-red-50 p-2" data-testid={`storyboard-error-${c.id}`}>
          <p className="text-xs font-medium text-red-700">Photo suggestions couldn’t load.</p>
          <div className="mt-1.5 flex gap-1.5">
            <Button data-testid={`storyboard-retry-${c.id}`} size="sm" onClick={runSuggest} className="h-7 rounded-lg bg-navy text-white text-xs"><RotateCw size={12} className="mr-1" /> Retry</Button>
            <Button data-testid={`storyboard-error-manual-${c.id}`} size="sm" variant="outline" onClick={() => { setError(false); loadCandidates(); }} className="h-7 rounded-lg text-xs">Choose manually</Button>
            <Button size="sm" variant="ghost" onClick={() => setError(false)} className="h-7 rounded-lg text-xs">Dismiss</Button>
          </div>
        </div>
      )}

      {noSealed && (
        <p className="mt-2 text-xs text-[#64748B]" data-testid={`storyboard-nosealed-${c.id}`}>
          Storyboards use your <b>sealed</b> photos. Seal your documentation to add photos here — you can still write a note below.
        </p>
      )}

      {/* Draft suggestions */}
      {openSuggestions.length > 0 && !archived && (
        <div className="mt-2.5" data-testid={`storyboard-suggestions-${c.id}`}>
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-[#64748B]">{openSuggestions.length} suggestion{openSuggestions.length > 1 ? "s" : ""} — review before adding</span>
            <Button data-testid={`storyboard-attach-selected-${c.id}`} size="sm" onClick={attachSelected} className="h-7 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs"><Check size={12} className="mr-1" /> Attach selected</Button>
          </div>
          <div className="mt-1.5 grid grid-cols-2 gap-2">
            {openSuggestions.map((s) => (
              <div key={s.photo_id} className="rounded-lg border border-dashed border-brand/50 bg-brand/[0.04] p-1.5" data-testid={`storyboard-suggestion-${c.id}-${s.photo_id}`}>
                <div className="flex items-start gap-1.5">
                  <input type="checkbox" data-testid={`storyboard-select-${c.id}-${s.photo_id}`} checked={!!selected[s.photo_id]} onChange={(e) => setSelected((v) => ({ ...v, [s.photo_id]: e.target.checked }))} className="mt-0.5 h-4 w-4 rounded accent-emerald-600" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-navy truncate">{s.label || s.room}</p>
                    {s.reason && <p className="text-[10px] text-[#64748B] truncate">{s.reason}</p>}
                  </div>
                </div>
                <div className="mt-1 flex gap-1">
                  <button data-testid={`storyboard-attach-${c.id}-${s.photo_id}`} onClick={() => attach(s.photo_id)} className="flex-1 rounded bg-emerald-600 text-white text-[10px] font-semibold py-1">Attach</button>
                  <button data-testid={`storyboard-dismiss-${c.id}-${s.photo_id}`} onClick={() => setDismissed((v) => ({ ...v, [s.photo_id]: true }))} className="rounded border border-[#E2E8F0] text-[#94A3B8] text-[10px] px-2 py-1">Dismiss</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Manual picker */}
      {candidates !== null && !archived && (
        <div className="mt-2.5 rounded-lg border border-[#E2E8F0] bg-white p-2" data-testid={`storyboard-picker-${c.id}`}>
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-[#64748B]">Choose sealed photos</span>
            <div className="flex items-center gap-1.5">
              {rooms.length > 1 && (
                <select data-testid={`storyboard-room-filter-${c.id}`} value={roomFilter} onChange={(e) => setRoomFilter(e.target.value)} className="rounded-md border border-[#E2E8F0] text-[11px] px-1.5 py-0.5">
                  <option value="all">All rooms</option>
                  {rooms.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              )}
              <button data-testid={`storyboard-picker-close-${c.id}`} onClick={() => setCandidates(null)} className="text-[#94A3B8]"><X size={14} /></button>
            </div>
          </div>
          {visibleCandidates.length === 0 ? (
            <p className="mt-1.5 text-[11px] text-[#94A3B8]">No sealed photos to add.</p>
          ) : (
            <div className="mt-1.5 grid grid-cols-3 gap-1.5 max-h-52 overflow-y-auto">
              {visibleCandidates.map((p) => (
                <button key={p.photo_id} data-testid={`storyboard-pick-${c.id}-${p.photo_id}`} onClick={() => attach(p.photo_id)} className="relative rounded-lg overflow-hidden border border-[#E2E8F0] hover:ring-2 hover:ring-brand">
                  <img src={fileUrl(p.storage_path)} alt="" className="w-full h-16 object-cover" />
                  <div className="px-1 py-0.5 text-[9px] text-[#475569] truncate text-left">{p.label || p.room}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Storyboard note (user's own words) */}
      <div className="mt-2.5">
        <Textarea data-testid={`storyboard-note-${c.id}`} value={note} onChange={(e) => setNote(e.target.value)}
          onBlur={() => note !== (c.storyboard_note || "") && onPatch(c.id, { storyboard_note: note })}
          disabled={archived} rows={2} className="rounded-lg text-sm bg-white" placeholder="Your notes about this charge (optional)…" />
      </div>

      {/* Include in dossier */}
      {!archived && (
        <label className="mt-2 flex items-center gap-2 text-xs text-[#475569] cursor-pointer" data-testid={`storyboard-include-label-${c.id}`}>
          <input type="checkbox" data-testid={`storyboard-include-${c.id}`} checked={!!c.storyboard_in_dossier}
            onChange={(e) => onPatch(c.id, { storyboard_in_dossier: e.target.checked })} className="h-4 w-4 rounded accent-brand" />
          Include this storyboard in the Exhibit Dossier export
          <InfoTip topic="exhibitDossier" />
        </label>
      )}
    </div>
  );
}
